/**
 * Shared utility functions for analytics functionality across the application.
 * This file contains functions used by analytics pages, components, and widgets.
 */

import moment from "moment";
import { WidgetTheme, TrendlineType, TimezoneOption } from "../types/analytics";

/**
 * Format a date object to YYYY-MM-DD string
 */
export function formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

/**
 * Get date from N days ago
 */
export function getDaysAgo(days: number): Date {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date;
}

/**
 * Get default date range (last 7 days ending yesterday)
 */
export function getDefaultDateRange(): { startDate: string; endDate: string } {
    const yesterday = getDaysAgo(1);
    const weekBeforeYesterday = getDaysAgo(7);
    
    return {
        startDate: formatDate(weekBeforeYesterday),
        endDate: formatDate(yesterday)
    };
}

/**
 * Given an applied date range, compute the immediately-preceding period of the same length.
 * E.g. 28 Apr – 5 May (8 days) → previous period is 20 Apr – 27 Apr (8 days).
 */
export function getPreviousPeriodDateRange(
    startDate: string,
    endDate: string
): { previousStartDate: string; previousEndDate: string; periodDays: number } {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const msPerDay = 24 * 60 * 60 * 1000;
    const periodDays = Math.round((end.getTime() - start.getTime()) / msPerDay) + 1;

    const previousEnd = new Date(start.getTime() - msPerDay);
    const previousStart = new Date(previousEnd.getTime() - (periodDays - 1) * msPerDay);

    return {
        previousStartDate: formatDate(previousStart),
        previousEndDate: formatDate(previousEnd),
        periodDays
    };
}

/**
 * Get theme configuration based on dark mode state
 */
export function getWidgetTheme(isDarkTheme: boolean): WidgetTheme {
    return {
        background: isDarkTheme ? "#1a1a1a" : "#ffffff",
        text: isDarkTheme ? "#ffffff" : "#000000",
        chartBg: isDarkTheme ? "#1a1a1a" : "#f5f5f5",
        border: isDarkTheme ? "#444" : "#ddd",
        positiveColor: isDarkTheme ? "#b6ff00" : "#28a745",
        negativeColor: isDarkTheme ? "#ff3a10" : "#dc3545"
    };
}

/**
 * Parse ISO date string to display format
 */
export function formatDisplayDate(isoDateString: string): string {
    const date = new Date(isoDateString);
    return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric"
    });
}

/**
 * Format a UTC date string for display based on the selected timezone.
 * The data is stored in UTC, so we parse it as UTC and format it in the target timezone.
 * 
 * @param utcDateString - Date string in format "YYYY-MM-DD" or ISO format (assumed UTC)
 * @param timezone - Timezone option ("local", "UTC", or IANA timezone name)
 * @param format - "short" for compact display (Jan 23), "long" for full (Jan 23, 2026)
 * @returns Formatted date string in the target timezone
 */
export function formatDateInTimezone(
    utcDateString: string,
    timezone: TimezoneOption,
    format: "short" | "long" = "short"
): string {
    // Parse the date as UTC (append Z if it's just a date without time)
    const dateStr = utcDateString.includes("T") ? utcDateString : `${utcDateString}T00:00:00Z`;
    const date = new Date(dateStr);
    
    if (isNaN(date.getTime())) {
        return utcDateString; // Return original if invalid
    }
    
    const options: Intl.DateTimeFormatOptions = {
        month: "short",
        day: "numeric",
        ...(format === "long" ? { year: "numeric" } : {})
    };
    
    if (timezone === "local") {
        // Use browser's local timezone
        return date.toLocaleDateString("en-US", options);
    } else if (timezone === "UTC") {
        // Explicitly use UTC
        return date.toLocaleDateString("en-US", { ...options, timeZone: "UTC" });
    } else {
        // Use specified IANA timezone
        try {
            return date.toLocaleDateString("en-US", { ...options, timeZone: timezone });
        } catch (e) {
            // Fallback to UTC if timezone is invalid
            return date.toLocaleDateString("en-US", { ...options, timeZone: "UTC" });
        }
    }
}

/**
 * Format a UTC datetime string for display based on the selected timezone.
 * Includes both date and time.
 * 
 * @param utcDateString - Date string in ISO format (assumed UTC)
 * @param timezone - Timezone option ("local", "UTC", or IANA timezone name)
 * @returns Formatted datetime string in the target timezone
 */
export function formatDateTimeInTimezone(
    utcDateString: string,
    timezone: TimezoneOption
): string {
    const dateStr = utcDateString.includes("T") ? utcDateString : `${utcDateString}T00:00:00Z`;
    const date = new Date(dateStr);
    
    if (isNaN(date.getTime())) {
        return utcDateString;
    }
    
    const options: Intl.DateTimeFormatOptions = {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true
    };
    
    if (timezone === "local") {
        return date.toLocaleString("en-US", options);
    } else if (timezone === "UTC") {
        return date.toLocaleString("en-US", { ...options, timeZone: "UTC" });
    } else {
        try {
            return date.toLocaleString("en-US", { ...options, timeZone: timezone });
        } catch (e) {
            return date.toLocaleString("en-US", { ...options, timeZone: "UTC" });
        }
    }
}

/**
 * Format a duration value into a compact, human-readable format like `1h 45m`.
 *
 * @param value - Numeric duration value
 * @param unit - Unit of the provided value (`hours` or `seconds`)
 * @returns Formatted duration string, or `-` for invalid values
 */
export function formatHumanDuration(
    value: number | null | undefined,
    unit: "hours" | "seconds" = "hours"
): string {
    if (value === null || value === undefined || !Number.isFinite(value)) {
        return "-";
    }

    const totalSeconds = unit === "hours" ? value * 3600 : value;
    const sign = totalSeconds < 0 ? "-" : "";
    const absoluteSeconds = Math.abs(totalSeconds);

    let roundedMinutes = Math.round(absoluteSeconds / 60);
    if (absoluteSeconds > 0 && roundedMinutes === 0) {
        roundedMinutes = 1;
    }

    const duration = moment.duration(roundedMinutes, "minutes");
    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();

    if (hours === 0) {
        return `${sign}${minutes}m`;
    }

    if (minutes === 0) {
        return `${sign}${hours}h`;
    }

    return `${sign}${hours}h ${minutes}m`;
}

/**
 * Calculate trendline values based on regression type
 * Takes an array of y-values and returns computed trendline y-values
 */
export function calculateTrendline(
    values: number[],
    trendlineType: TrendlineType
): number[] {
    const n = values.length;
    if (n === 0) return [];
    
    // x values are indices (0, 1, 2, ...)
    const xValues = values.map((_, i) => i);
    
    switch (trendlineType) {
        case TrendlineType.CONNECTED:
            // Just return the original values (connected line)
            return values;
        
        case TrendlineType.MOVING_AVERAGE:
            return movingAverage(values, 3);
            
        case TrendlineType.EMA:
            return exponentialMovingAverage(values, 0.3);

        case TrendlineType.LINEAR:
            return linearRegression(xValues, values);
            
        case TrendlineType.LOGARITHMIC:
            return logarithmicRegression(xValues, values);
            
        case TrendlineType.EXPONENTIAL:
            return exponentialRegression(xValues, values);
            
        case TrendlineType.POLYNOMIAL:
            return polynomialRegression(xValues, values, 2);
            
        case TrendlineType.LOESS:
            return loessSmoothing(values, 0.3);
            
        case TrendlineType.SPLINE:
            return cubicSpline(xValues, values);
            
        default:
            return values;
    }
}

/**
 * Linear regression: y = mx + b
 */
function linearRegression(x: number[], y: number[]): number[] {
    const n = x.length;
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
    
    for (let i = 0; i < n; i++) {
        sumX += x[i];
        sumY += y[i];
        sumXY += x[i] * y[i];
        sumX2 += x[i] * x[i];
    }
    
    const m = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const b = (sumY - m * sumX) / n;
    
    return x.map(xi => m * xi + b);
}

/**
 * Logarithmic regression: y = a + b*ln(x)
 * Uses x+1 to avoid ln(0)
 */
function logarithmicRegression(x: number[], y: number[]): number[] {
    const n = x.length;
    const lnX = x.map(xi => Math.log(xi + 1));
    
    let sumLnX = 0, sumY = 0, sumLnXY = 0, sumLnX2 = 0;
    
    for (let i = 0; i < n; i++) {
        sumLnX += lnX[i];
        sumY += y[i];
        sumLnXY += lnX[i] * y[i];
        sumLnX2 += lnX[i] * lnX[i];
    }
    
    const b = (n * sumLnXY - sumLnX * sumY) / (n * sumLnX2 - sumLnX * sumLnX);
    const a = (sumY - b * sumLnX) / n;
    
    return x.map(xi => a + b * Math.log(xi + 1));
}

/**
 * Exponential regression: y = a * e^(bx)
 * Uses linear regression on ln(y) to find coefficients
 */
function exponentialRegression(x: number[], y: number[]): number[] {
    const n = x.length;
    
    // Filter out non-positive values for ln calculation
    const validIndices: number[] = [];
    const lnY: number[] = [];
    
    for (let i = 0; i < n; i++) {
        if (y[i] > 0) {
            validIndices.push(i);
            lnY.push(Math.log(y[i]));
        }
    }
    
    if (validIndices.length < 2) {
        // Not enough valid data points, fall back to linear
        return linearRegression(x, y);
    }
    
    const validX = validIndices.map(i => x[i]);
    
    // Linear regression on ln(y) = ln(a) + bx
    let sumX = 0, sumLnY = 0, sumXLnY = 0, sumX2 = 0;
    
    for (let i = 0; i < validX.length; i++) {
        sumX += validX[i];
        sumLnY += lnY[i];
        sumXLnY += validX[i] * lnY[i];
        sumX2 += validX[i] * validX[i];
    }
    
    const vn = validX.length;
    const b = (vn * sumXLnY - sumX * sumLnY) / (vn * sumX2 - sumX * sumX);
    const lnA = (sumLnY - b * sumX) / vn;
    const a = Math.exp(lnA);
    
    return x.map(xi => a * Math.exp(b * xi));
}

/**
 * Polynomial regression: y = a0 + a1*x + a2*x^2 + ... + an*x^n
 * Uses matrix solution (simplified for degree 2)
 */
function polynomialRegression(x: number[], y: number[], degree: number): number[] {
    const n = x.length;
    
    // For simplicity, implement degree 2 polynomial: y = a + bx + cx^2
    if (degree !== 2 || n < 3) {
        return linearRegression(x, y);
    }
    
    // Build normal equations for least squares
    let sumX = 0, sumX2 = 0, sumX3 = 0, sumX4 = 0;
    let sumY = 0, sumXY = 0, sumX2Y = 0;
    
    for (let i = 0; i < n; i++) {
        const xi = x[i];
        const xi2 = xi * xi;
        const yi = y[i];
        
        sumX += xi;
        sumX2 += xi2;
        sumX3 += xi2 * xi;
        sumX4 += xi2 * xi2;
        sumY += yi;
        sumXY += xi * yi;
        sumX2Y += xi2 * yi;
    }
    
    // Solve 3x3 system using Cramer's rule
    // [n,    sumX,  sumX2 ] [a]   [sumY  ]
    // [sumX, sumX2, sumX3 ] [b] = [sumXY ]
    // [sumX2,sumX3, sumX4 ] [c]   [sumX2Y]
    
    const det = n * (sumX2 * sumX4 - sumX3 * sumX3) 
              - sumX * (sumX * sumX4 - sumX3 * sumX2) 
              + sumX2 * (sumX * sumX3 - sumX2 * sumX2);
    
    if (Math.abs(det) < 1e-10) {
        return linearRegression(x, y);
    }
    
    const detA = sumY * (sumX2 * sumX4 - sumX3 * sumX3)
               - sumX * (sumXY * sumX4 - sumX3 * sumX2Y)
               + sumX2 * (sumXY * sumX3 - sumX2 * sumX2Y);
    
    const detB = n * (sumXY * sumX4 - sumX3 * sumX2Y)
               - sumY * (sumX * sumX4 - sumX3 * sumX2)
               + sumX2 * (sumX * sumX2Y - sumXY * sumX2);
    
    const detC = n * (sumX2 * sumX2Y - sumXY * sumX3)
               - sumX * (sumX * sumX2Y - sumXY * sumX2)
               + sumY * (sumX * sumX3 - sumX2 * sumX2);
    
    const a = detA / det;
    const b = detB / det;
    const c = detC / det;
    
    return x.map(xi => a + b * xi + c * xi * xi);
}

/**
 * Simple Moving Average with centered window
 * @param values - array of y values
 * @param windowSize - number of points to average (should be odd for centering)
 */
function movingAverage(values: number[], windowSize = 3): number[] {
    const n = values.length;
    if (n === 0) return [];
    
    const halfWindow = Math.floor(windowSize / 2);
    const result: number[] = [];
    
    for (let i = 0; i < n; i++) {
        const start = Math.max(0, i - halfWindow);
        const end = Math.min(n - 1, i + halfWindow);
        let sum = 0;
        let count = 0;
        
        for (let j = start; j <= end; j++) {
            sum += values[j];
            count++;
        }
        
        result.push(sum / count);
    }
    
    return result;
}

/**
 * Exponential Moving Average (EMA)
 * @param values - array of y values
 * @param alpha - smoothing factor (0 < alpha <= 1), higher = more weight on recent values
 */
function exponentialMovingAverage(values: number[], alpha = 0.3): number[] {
    const n = values.length;
    if (n === 0) return [];
    
    const result: number[] = [values[0]];
    
    for (let i = 1; i < n; i++) {
        // EMA = alpha * current + (1 - alpha) * previous EMA
        const ema = alpha * values[i] + (1 - alpha) * result[i - 1];
        result.push(ema);
    }
    
    return result;
}

/**
 * LOESS/LOWESS (Locally Estimated Scatterplot Smoothing)
 * Uses tricube weighting for local polynomial regression
 * @param values - array of y values
 * @param span - fraction of data to use for each local regression (0 < span <= 1)
 */
function loessSmoothing(values: number[], span = 0.3): number[] {
    const n = values.length;
    if (n === 0) return [];
    
    const windowSize = Math.max(3, Math.floor(n * span));
    const halfWindow = Math.floor(windowSize / 2);
    const result: number[] = [];
    
    for (let i = 0; i < n; i++) {
        const start = Math.max(0, i - halfWindow);
        const end = Math.min(n - 1, i + halfWindow);
        
        // Calculate max distance for normalization
        const maxDist = Math.max(i - start, end - i) || 1;
        
        // Weighted least squares with tricube weights
        let sumW = 0, sumWX = 0, sumWY = 0, sumWX2 = 0, sumWXY = 0;
        
        for (let j = start; j <= end; j++) {
            // Tricube weight: (1 - |d|^3)^3
            const dist = Math.abs(j - i) / maxDist;
            const weight = Math.pow(1 - Math.pow(dist, 3), 3);
            
            sumW += weight;
            sumWX += weight * j;
            sumWY += weight * values[j];
            sumWX2 += weight * j * j;
            sumWXY += weight * j * values[j];
        }
        
        // Solve weighted linear regression
        const denom = sumW * sumWX2 - sumWX * sumWX;
        
        if (Math.abs(denom) < 1e-10) {
            result.push(sumWY / sumW);
        } else {
            const slope = (sumW * sumWXY - sumWX * sumWY) / denom;
            const intercept = (sumWY - slope * sumWX) / sumW;
            result.push(slope * i + intercept);
        }
    }
    
    return result;
}

/**
 * Cubic Spline Interpolation
 * Creates smooth curves through all data points
 * @param x - array of x values (indices)
 * @param y - array of y values
 */
function cubicSpline(x: number[], y: number[]): number[] {
    const n = x.length;
    if (n < 3) return y.slice();
    
    // Natural cubic spline using Thomas algorithm for tridiagonal system
    const h: number[] = [];
    for (let i = 0; i < n - 1; i++) {
        h.push(x[i + 1] - x[i]);
    }
    
    // Build tridiagonal system for second derivatives
    const alpha: number[] = [0];
    for (let i = 1; i < n - 1; i++) {
        alpha.push(
            (3 / h[i]) * (y[i + 1] - y[i]) - (3 / h[i - 1]) * (y[i] - y[i - 1])
        );
    }
    
    // Solve using Thomas algorithm
    const l: number[] = [1];
    const mu: number[] = [0];
    const z: number[] = [0];
    
    for (let i = 1; i < n - 1; i++) {
        l.push(2 * (x[i + 1] - x[i - 1]) - h[i - 1] * mu[i - 1]);
        mu.push(h[i] / l[i]);
        z.push((alpha[i] - h[i - 1] * z[i - 1]) / l[i]);
    }
    
    l.push(1);
    z.push(0);
    
    // Second derivatives (c coefficients)
    const c: number[] = new Array(n).fill(0);
    for (let j = n - 2; j >= 0; j--) {
        c[j] = z[j] - mu[j] * c[j + 1];
    }
    
    // Calculate b and d coefficients
    const b: number[] = [];
    const d: number[] = [];
    for (let i = 0; i < n - 1; i++) {
        b.push((y[i + 1] - y[i]) / h[i] - h[i] * (c[i + 1] + 2 * c[i]) / 3);
        d.push((c[i + 1] - c[i]) / (3 * h[i]));
    }
    
    // Evaluate spline at each original x point
    const result: number[] = [];
    for (let i = 0; i < n; i++) {
        // Find which spline segment to use
        let seg = 0;
        for (let j = 0; j < n - 1; j++) {
            if (x[i] >= x[j] && x[i] <= x[j + 1]) {
                seg = j;
                break;
            }
        }
        if (i === n - 1) seg = n - 2;
        
        const dx = x[i] - x[seg];
        const val = y[seg] + b[seg] * dx + c[seg] * dx * dx + d[seg] * dx * dx * dx;
        result.push(val);
    }

    return result;
}

/**
 * Strip common leading indentation from a multiline string.
 * Prevents Markdown parsers treating indented template literals as code blocks.
 */
export function dedent(text: string): string {
    const lines = text.split("\n");
    const indent = lines
        .filter(line => line.trim().length > 0)
        .reduce((min, line) => {
            const match = line.match(/^( *)/);
            return match ? Math.min(min, match[1].length) : min;
        }, Infinity);
    const safeIndent = isFinite(indent) ? indent : 0;
    return lines.map(line => line.slice(safeIndent)).join("\n");
}
